import { useCallback, useRef } from 'react';
import { webchatApi } from '../services/api';
import { McpClient } from '../services/mcp-client';
import { store } from '../state/store';
import { setOrchestratorRef } from '../components/messageList/MessageList.helpers';
import type { McpWidgetRuntimeRefs } from './types';

interface WidgetRuntime {
  refs: McpWidgetRuntimeRefs;
  closeMcpSession(): Promise<void>;
  fetchAnonymousToken(): Promise<void>;
}

export function useWidgetRuntime(): WidgetRuntime {
  const mcpClientRef = useRef<McpClient | null>(null);
  const orchestratorRef = useRef<import('../services/orchestrator').ChatOrchestrator | null>(null);
  const mcpReadyPromiseRef = useRef<Promise<void> | null>(null);
  const anonymousTokenPromiseRef = useRef<Promise<void> | null>(null);
  const healthCheckPromiseRef = useRef<Promise<void> | null>(null);

  const closeMcpSession = useCallback(async (): Promise<void> => {
    if (mcpClientRef.current) {
      await mcpClientRef.current.close();
      mcpClientRef.current = null;
    }

    orchestratorRef.current = null;
    mcpReadyPromiseRef.current = null;
    setOrchestratorRef(null);
  }, []);

  const fetchAnonymousToken = useCallback(async (): Promise<void> => {
    try {
      const result = await webchatApi.authAnonymous();
      store.setAuth({ status: 'authenticated', email: null, token: result.token ?? null });
      console.debug('[mcp-chat-widget] Anonymous session established');
    } catch (err) {
      console.warn('[mcp-chat-widget] Failed to get anonymous token:', err);
    }
  }, []);

  return {
    refs: {
      mcpClientRef,
      orchestratorRef,
      mcpReadyPromiseRef,
      anonymousTokenPromiseRef,
      healthCheckPromiseRef,
    },
    closeMcpSession,
    fetchAnonymousToken,
  };
}
